import { EventAgendaItem } from '~/types';

export interface AgendaGroup {
    key: string;
    /** "Monday, September 14" */
    label: string;
    /** "Day 1" */
    dayLabel: string;
    /** "Mon, 14 Sep" */
    shortLabel: string;
    sort: number;
    items: EventAgendaItem[];
}

export const parseDate = (value?: string | null): Date | null => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const time = (d: Date) =>
    d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();

/** "7:00 am - 8:30 am" (or a single time when there is no end). */
export const sessionTime = (item: EventAgendaItem): string => {
    const start = parseDate(item.startTime);
    const end = parseDate(item.endTime);
    if (start) return end ? `${time(start)} - ${time(end)}` : time(start);
    if (item.time) return item.endTime ? `${item.time} - ${item.endTime}` : item.time;
    return '';
};

/** "7:00 am - 8:30 am | Monday, September 14" */
export const sessionDateTime = (item: EventAgendaItem): string => {
    const t = sessionTime(item);
    const d = parseDate(item.startTime) || parseDate(item.date);
    const day = d
        ? d.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })
        : '';
    return [t, day].filter(Boolean).join(' | ');
};

/** Sortable timestamp (ms). Undated sessions sort last. */
export const sortValue = (item: EventAgendaItem): number => {
    const start = parseDate(item.startTime) || parseDate(`${item.date || ''}T${item.time || ''}`);
    return start ? start.getTime() : Number.MAX_SAFE_INTEGER;
};

export const speakerName = (item: EventAgendaItem): string =>
    item.responsibleParson || item.speaker || '';

const dayKey = (item: EventAgendaItem): string => {
    const start = parseDate(item.startTime);
    if (start) return start.toISOString().slice(0, 10);
    return item.date || '__none__';
};

/** Group agenda items by calendar day, sorted ascending, with day numbering. */
export const buildGroups = (items: EventAgendaItem[]): AgendaGroup[] => {
    const map = new Map<string, AgendaGroup>();

    items.forEach((item) => {
        const key = dayKey(item);
        if (!map.has(key)) {
            const d = parseDate(item.startTime) || parseDate(item.date);
            map.set(key, {
                key,
                label: d
                    ? d.toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })
                    : 'Schedule',
                shortLabel: d
                    ? d.toLocaleDateString('default', { weekday: 'short', day: 'numeric', month: 'short' })
                    : 'Schedule',
                dayLabel: '',
                sort: Number.MAX_SAFE_INTEGER,
                items: [],
            });
        }
        const group = map.get(key)!;
        group.items.push(item);
        group.sort = Math.min(group.sort, sortValue(item));
    });

    const groups = Array.from(map.values()).sort((a, b) => a.sort - b.sort);
    groups.forEach((group, index) => {
        group.items.sort((a, b) => sortValue(a) - sortValue(b));
        group.dayLabel = `Day ${index + 1}`;
    });
    return groups;
};

const pad = (n: number) => String(n).padStart(2, '0');
const toIcsDate = (d: Date) =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;

/** Build and download an .ics file for a single session. */
export const addToCalendar = (item: EventAgendaItem, opts: { location?: string; eventTitle?: string } = {}) => {
    const start = parseDate(item.startTime);
    if (!start) return;
    const end = parseDate(item.endTime) || new Date(start.getTime() + 30 * 60 * 1000);
    const escape = (s: string) => (s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');

    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Yekonga//Events//EN',
        'BEGIN:VEVENT',
        `UID:${item.id || Math.random().toString(36).slice(2)}@yekonga`,
        `DTSTAMP:${toIcsDate(new Date())}`,
        `DTSTART:${toIcsDate(start)}`,
        `DTEND:${toIcsDate(end)}`,
        `SUMMARY:${escape(item.title)}`,
        opts.location ? `LOCATION:${escape(opts.location)}` : '',
        (item.description || opts.eventTitle)
            ? `DESCRIPTION:${escape([opts.eventTitle, speakerName(item), item.description || ''].filter(Boolean).join(' — '))}`
            : '',
        'END:VEVENT',
        'END:VCALENDAR',
    ].filter(Boolean);

    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(item.title || 'session').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};
